import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useCurrentAccount } from '@/features/auth/hooks/useCurrentAccount'
import { useAuth } from '@/features/auth/context/authProvider'
import Alert from '@/shared/components/Alert/Alert'
import Button from '@/shared/components/Button/Button'
import Chip from '@/shared/components/Chip/Chip'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import Select from '@/shared/components/Select/Select'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import TextField from '@/shared/components/TextField/TextField'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import { OptionType } from '@/shared/api/BaseAPI.model'
import SettingsPanel from '@/features/settings/components/SettingsPanel'
import { DeliveryConnectionsContext } from './api'
import { deliveryConnectionsKey } from './types'
import type { AcceptanceMode, DeliveryProvider } from './types'
import styles from './DeliveryConnections.module.scss'

/** What the backend accepts for a provider store id (UpsertGlovoIntegrationDto). */
const STORE_ID_MAX_LENGTH = 255

interface CardProps {
  restaurantId: string
  restaurantName: string
  provider: DeliveryProvider
}

function ProviderCard({ restaurantId, restaurantName, provider }: CardProps) {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const client = useContext(DeliveryConnectionsContext)
  const queryClient = useQueryClient()
  const key = deliveryConnectionsKey(currentUser?.uid, restaurantId, provider)
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => client.get(restaurantId, provider, signal),
    retry: false
  })
  const capabilities = useQuery({
    queryKey: [...key, 'capabilities'],
    queryFn: () => client.capabilities(restaurantId),
    enabled: provider === 'uber-eats',
    retry: false
  })
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [storeId, setStoreId] = useState('')
  const [draftId, setDraftId] = useState('')
  const [mode, setMode] = useState<AcceptanceMode>('MANUAL')
  const [message, setMessage] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<'activate' | 'disconnect' | null>(null)
  const popup = useRef<Window | null>(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      popup.current?.close()
    }
  }, [])
  const savedMode = query.data?.acceptanceMode
  useEffect(() => {
    if (savedMode) setMode(savedMode)
  }, [savedMode])

  const session = useQuery({
    queryKey: [...key, 'session', sessionId],
    queryFn: ({ signal }) => client.session(restaurantId, sessionId!, signal),
    enabled: !!sessionId,
    retry: false,
    refetchInterval: q =>
      q.state.status === 'error' ||
      (q.state.data !== undefined && q.state.data.status !== 'waiting')
        ? false
        : 2000
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
  }
  const action = useMutation({
    mutationFn: async (
      kind:
        | 'start'
        | 'activate'
        | 'verify'
        | 'disconnect'
        | 'draft'
        | 'mode'
        | 'cancel'
    ) => {
      setMessage(null)
      if (kind === 'cancel') {
        await client.cancel(restaurantId, sessionId!)
        setSessionId(null)
        popup.current?.close()
        return
      }
      if (kind === 'start') {
        const result = await client.start(restaurantId)
        if (!mounted.current) {
          popup.current?.close()
          return
        }
        setSessionId(result.sessionId)
        setStoreId('')
        if (popup.current) {
          popup.current.opener = null
          popup.current.location.href = result.authorizationUrl
        }
        return
      }
      if (kind === 'draft') {
        await client.save(restaurantId, 'glovo', {
          externalStoreId: draftId.trim(),
          enabled: false,
          acceptanceMode: 'AUTOMATIC'
        })
        setDraftId('')
        setMessage('DRAFT_SAVED')
      } else if (kind === 'mode') {
        /*
          Only the mode, through the endpoint that changes only the mode.
          Sending the whole connection back would carry this panel's copy of
          `enabled` with it, and that copy goes stale the moment anyone else
          connects or disconnects the venue.
        */
        await client.setAcceptanceMode(restaurantId, mode)
        setMessage('SAVED')
      } else if (kind === 'disconnect') {
        if (provider === 'uber-eats') await client.disconnect(restaurantId)
        else if (query.data)
          await client.save(restaurantId, provider, {
            ...query.data,
            enabled: false
          })
        setMessage('DISCONNECTED')
      } else {
        const result =
          kind === 'activate'
            ? await client.activate(restaurantId, sessionId!, storeId, mode)
            : await client.verify(restaurantId)
        let resultMessage = 'INACTIVE'
        if (result.pending) resultMessage = 'PENDING'
        if (result.active) resultMessage = 'VERIFIED'
        setMessage(resultMessage)
        if (kind === 'activate') {
          setSessionId(null)
          popup.current?.close()
        }
      }
      await refresh()
    }
  })
  const connection = query.data
  const busy = action.isPending
  const label = provider === 'uber-eats' ? 'Uber Eats' : 'Glovo'
  let status = 'NOT_CONFIGURED'
  if (connection) status = 'DISABLED'
  if (connection?.enabled) status = 'ENABLED'
  if (query.isError) status = 'ERROR'
  const failCode = (action.error as { response?: { status?: number } } | null)
    ?.response?.status
  const errorText = ({ 403: 'NO_PERMISSION', 409: 'CONFLICT' } as Record<number, string>)[failCode ?? 0] ?? 'REQUEST_FAILED'
  let confirmMessage = provider === 'uber-eats' ? 'DELIVERY.CONFIRM_DISCONNECT' : 'DELIVERY.CONFIRM_PAUSE'
  if (confirm === 'activate') confirmMessage = 'DELIVERY.CONFIRM_ACTIVATE'

  const modeOptions: OptionType[] = useMemo(
    () => [
      { value: 'MANUAL', label: t('DELIVERY.MANUAL') },
      { value: 'AUTOMATIC', label: t('DELIVERY.AUTOMATIC') }
    ],
    [t]
  )
  const storeOptions: OptionType[] = (session.data?.stores ?? []).map(store => ({
    value: store.id,
    label: `${store.name} — ${store.address}`
  }))
  const pickOne = (next: selectValueType): string | null =>
    !next || Array.isArray(next) ? null : next.value

  return (
    <section className={styles.card} aria-label={label}>
      <div className={styles.header}>
        <h3 className="title-bold-small">{label}</h3>
        {query.isPending ? (
          <Skeleton width="8rem" height="1.5rem" />
        ) : (
          <Chip
            label={t(`DELIVERY.${status}`)}
            color={connection?.enabled ? 'secondary' : 'primary'}
          />
        )}
      </div>
      <p className="body-medium">
        {t(
          `DELIVERY.${provider === 'uber-eats' ? 'UBER_DESCRIPTION' : 'GLOVO_DESCRIPTION'}`
        )}
      </p>
      {connection && (
        <dl className={`body-medium ${styles.facts}`}>
          <dt>{t('DELIVERY.STORE_ID')}</dt>
          <dd>{connection.externalStoreId}</dd>
          <dt>{t('DELIVERY.MENU_VENUE')}</dt>
          <dd>{restaurantName}</dd>
        </dl>
      )}
      {query.isError && (
        <Alert severity="error" message={t('DELIVERY.LOAD_FAILED')} />
      )}
      {message && (
        <Alert
          severity={
            message === 'VERIFIED' || message === 'SAVED' ? 'success' : 'info'
          }
          message={t(`DELIVERY.${message}`)}
        />
      )}
      {action.isError && (
        <Alert severity="error" message={t(`DELIVERY.${errorText}`)} />
      )}
      <div className={styles.actions}>
        <Button
          label={t('DELIVERY.REFRESH')}
          variant="text"
          color="primary"
          disabled={busy || query.isFetching}
          onClick={() => {
            refresh().catch(() => undefined)
            if (provider === 'uber-eats') capabilities.refetch().catch(() => undefined)
          }}
        />
        {provider === 'uber-eats' && (
          <Button
            label={t('DELIVERY.CONNECT_UBER')}
            variant="contained"
            color="primary"
            // Also off while an attempt is open: a second one would leave the
            // first window and its server-side session with nothing pointing
            // at them, and that session is holding a merchant token.
            disabled={
              busy ||
              query.isPending ||
              query.isError ||
              !capabilities.data?.oauthAvailable ||
              !!connection?.enabled ||
              !!sessionId
            }
            onClick={() => {
              popup.current?.close()
              popup.current = window.open(
                'about:blank',
                '_blank',
                'popup,width=600,height=760'
              )
              if (!popup.current) {
                setMessage('POPUP_BLOCKED')
                return
              }
              popup.current.opener = null
              action.mutate('start', { onError: () => popup.current?.close() })
            }}
          />
        )}
        {provider === 'uber-eats' && connection && (
          <Button
            label={t('DELIVERY.VERIFY')}
            variant="outlined"
            color="primary"
            disabled={busy}
            onClick={() => action.mutate('verify')}
          />
        )}
        {connection && (
          <Button
            label={t(
              provider === 'uber-eats'
                ? 'DELIVERY.DISCONNECT'
                : 'DELIVERY.PAUSE'
            )}
            variant="outlined"
            color="primary"
            disabled={busy || (!connection.enabled && provider === 'glovo')}
            onClick={() => setConfirm('disconnect')}
          />
        )}
      </div>
      {provider === 'uber-eats' &&
        !capabilities.isPending &&
        !capabilities.data?.oauthAvailable && (
          <Alert
            severity="info"
            message={t(
              capabilities.isError
                ? 'DELIVERY.OAUTH_UNAVAILABLE'
                : 'DELIVERY.OAUTH_SETUP'
            )}
          />
        )}
      {provider === 'uber-eats' && (
        <Select
          label={t('DELIVERY.ACCEPTANCE')}
          options={modeOptions}
          value={modeOptions.find(option => option.value === mode) ?? null}
          disabled={busy}
          onChange={next => {
            const picked = pickOne(next)
            if (picked) setMode(picked as AcceptanceMode)
          }}
          focused
          hideClearIcon
          readonly
        />
      )}
      {provider === 'uber-eats' && (
        <p className={`body-small ${styles.hint}`}>
          {t('DELIVERY.ACCEPTANCE_HINT')}
        </p>
      )}
      {provider === 'uber-eats' && connection && (
        <Button
          label={t('DELIVERY.SAVE_MODE')}
          variant="text"
          color="primary"
          disabled={busy}
          onClick={() => action.mutate('mode')}
        />
      )}
      {sessionId && (
        <div className={styles.selection}>
          {session.data?.status === 'waiting' && (
            <p className="body-medium" role="status">
              {t('DELIVERY.WAITING_AUTH')}
            </p>
          )}
          {(session.isError || session.data?.status === 'failed') && (
            <Alert severity="error" message={t('DELIVERY.AUTH_FAILED')} />
          )}
          {session.data?.status === 'ready' && (
            <>
              {storeOptions.length === 0 ? (
                <Alert severity="info" message={t('DELIVERY.NO_STORES')} />
              ) : (
                <>
                  <Select
                    label={t('DELIVERY.SELECT_STORE')}
                    options={storeOptions}
                    value={
                      storeOptions.find(option => option.value === storeId) ??
                      null
                    }
                    disabled={busy}
                    onChange={next => setStoreId(pickOne(next) ?? '')}
                    focused
                    hideClearIcon
                    readonly
                  />
                  <Button
                    label={t('DELIVERY.ACTIVATE')}
                    variant="contained"
                    color="primary"
                    disabled={busy || !storeId}
                    onClick={() => setConfirm('activate')}
                  />
                </>
              )}
            </>
          )}
          <Button
            label={t('DELIVERY.CANCEL_AUTH')}
            variant="text"
            color="primary"
            disabled={busy}
            onClick={() => action.mutate('cancel')}
          />
        </div>
      )}
      {provider === 'glovo' && (
        <>
          <Alert severity="info" message={t('DELIVERY.GLOVO_SETUP')} />
          {!connection?.enabled && (
            <div className={styles.selection}>
              <TextField
                label={t('DELIVERY.STORE_ID')}
                value={draftId}
                disabled={busy}
                onChange={event =>
                  setDraftId(event.target.value.slice(0, STORE_ID_MAX_LENGTH))
                }
              />
              <Button
                label={t('DELIVERY.SAVE_DRAFT')}
                variant="outlined"
                color="primary"
                disabled={busy || !draftId.trim()}
                onClick={() => action.mutate('draft')}
              />
            </div>
          )}
        </>
      )}
      <ConfirmationModal
        isOpen={confirm !== null}
        title={t(
          confirm === 'activate' ? 'DELIVERY.ACTIVATE' : 'DELIVERY.DISCONNECT'
        )}
        message={t(confirmMessage, { name: restaurantName })}
        resetsFormDirty={false}
        // Opened from inside the settings modal, whose centring transform would
        // otherwise trap it in that box (Modal.tsx).
        disablePortal={false}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          action.mutate(confirm === 'activate' ? 'activate' : 'disconnect')
          setConfirm(null)
        }}
      />
    </section>
  )
}

export default function DeliveryConnections() {
  const { t } = useTranslation()
  const { restaurantId, restaurants, isLoading } = useCurrentAccount()
  // Freeze the target for this screen, even if another browser tab switches venue.
  const [targetId] = useState(restaurantId)
  const venue = restaurants.find(
    restaurant => String(restaurant.id) === targetId
  )

  return (
    <SettingsPanel lead={t('DELIVERY.LEAD')}>
      {!targetId ? (
        <Alert severity="error" message={t('DELIVERY.NO_VENUE')} />
      ) : (
        <div className={styles.grid}>
          {/*
            The venue list arrives separately from the id, and until it does
            there is no name to print. Falling back to the id put a raw UUID in
            the panel heading and, worse, in the sentence asking whether to
            disconnect this venue.
          */}
          {isLoading || !venue ? (
            <Skeleton height="4rem" />
          ) : (
            <>
              <Alert
                severity="info"
                message={t('DELIVERY.VENUE_HINT', { name: venue.name })}
              />
              <p className={`body-small ${styles.hint}`}>
                {t('DELIVERY.VENUE_ID')}: {targetId}
              </p>
              {(['uber-eats', 'glovo'] as const).map(provider => (
                <ProviderCard
                  key={`${targetId}:${provider}`}
                  provider={provider}
                  restaurantId={targetId}
                  restaurantName={venue.name}
                />
              ))}
            </>
          )}
        </div>
      )}
    </SettingsPanel>
  )
}
